import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { hivesOverviewRoute } from '@/features/HivesOverview/pages/HivesOverviewHivesTable'
import { AccountLookupForm } from '@/features/SalesforceAccounts/components/AccountLookupForm'
import { useCanManageSalesforceLink } from '@/features/SalesforceAccounts/hooks/useCanManageSalesforceLink'
import { ResolveIngestLink } from '@/features/SalesforceIngest/components/ResolveIngestLink'
import { salesforceAccountsRootRoute } from '@/router/rootRoutes'
import { Skeleton, Text } from '@chakra-ui/react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

export const salesforceAccountsIndexRoute = createRoute({
  getParentRoute: () => salesforceAccountsRootRoute,
  path: '/',
  component: SalesforceAccountsIndexPage,
})

/**
 * The entry point for the reverse lookup: type a Salesforce account id, land
 * on that account's organisations.
 *
 * Gated on the same read role as the account page it leads to, through the one
 * `useCanManageSalesforceLink` hook so both pages move together.
 */
export function SalesforceAccountsIndexPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const { canRead, isResolved } = useCanManageSalesforceLink()
  const navigate = useNavigate()

  // `isResolved` first, always: the roles query is still pending on the first
  // render of a hard page load, so redirecting on `canRead` alone bounces
  // every deep link before the viewer's roles have arrived.
  useEffect(() => {
    if (isResolved && !canRead) {
      void navigate({ to: hivesOverviewRoute.to })
    }
  }, [canRead, isResolved, navigate])

  if (isResolved && !canRead) {
    return null
  }

  return (
    <PageLayout
      title={String(t('salesforceAccounts.index.pageTitle'))}
      description={
        <Text color="text.text-subtle">
          {String(t('salesforceAccounts.index.description'))}
        </Text>
      }
      action={<ResolveIngestLink />}
      maxWidth="8xl"
    >
      {isResolved ? <AccountLookupForm /> : <Skeleton h={20} w="100%" />}
    </PageLayout>
  )
}
